import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { listMeetings } from '../../../api/meetings/meetings';
import TodayMeetingsCard from '../TodayMeetingsCard';
import PendingMeetingsCard from '../PendingMeetingsCard';
import './OverviewMobilePanels.css';

const convertToDate = (dateValue) => {
  if (!dateValue) return null;

  try {
    if (dateValue instanceof Date) return dateValue;
    if (dateValue?.toDate && typeof dateValue.toDate === 'function') return dateValue.toDate();
    if (dateValue?.seconds) return new Date(dateValue.seconds * 1000);
    const date = new Date(dateValue);
    return Number.isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
};

const useMeetingsForMobilePanels = () => {
  const { companyData } = useAuth();
  const [meetings, setMeetings] = useState([]);

  useEffect(() => {
    if (!companyData?.id) return undefined;

    let cancelled = false;
    const loadMeetings = async () => {
      try {
        const rows = await listMeetings({ companyId: companyData.id, archived: false });
        if (cancelled) return;
        const now = new Date();
        now.setHours(0, 0, 0, 0);

        const list = rows
          .map((data) => {
            const dateTime = convertToDate(data.dateTime);
            if (!dateTime) return null;
            return { ...data, dateTime };
          })
          .filter((meeting) => {
            if (!meeting) return false;
            const status = (meeting.status || '').toLowerCase();
            if (status === 'cancelled' || status === 'canceled') return false;
            if (meeting.archived || meeting.archivedAt) return false;

            const day = new Date(meeting.dateTime);
            day.setHours(0, 0, 0, 0);
            return day >= now;
          })
          .sort((a, b) => a.dateTime - b.dateTime);

        setMeetings(list);
      } catch (error) {
        console.error('Error loading meetings for mobile panels:', error);
      }
    };

    loadMeetings();
    const poll = setInterval(loadMeetings, 12000);
    return () => {
      cancelled = true;
      clearInterval(poll);
    };
  }, [companyData?.id]);

  const todayMeetings = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return meetings.filter((meeting) => meeting.dateTime >= today && meeting.dateTime < tomorrow);
  }, [meetings]);

  const pendingMeetings = useMemo(
    () => meetings.filter((meeting) => (meeting.status || '').toLowerCase() === 'pending'),
    [meetings],
  );

  return { meetings, todayMeetings, pendingMeetings };
};

export const useMeetingMobileNavCounts = () => {
  const { todayMeetings, pendingMeetings } = useMeetingsForMobilePanels();

  return useMemo(() => ({
    todayCount: todayMeetings.length,
    pendingCount: pendingMeetings.length,
  }), [pendingMeetings.length, todayMeetings.length]);
};

export const MeetingsMobileTodayPanel = () => {
  const navigate = useNavigate();
  const { todayMeetings } = useMeetingsForMobilePanels();

  const handleMeetingClick = (meeting) => {
    if (!meeting?.id) return;
    const meetingSegment = meeting.shortSlug ?? meeting.id;
    navigate(`/app/overview/meeting/${meetingSegment}`, { state: { meeting } });
  };

  return (
    <div className="overview-mobile-panel-page">
      <TodayMeetingsCard
        todayMeetings={todayMeetings}
        onMeetingClick={handleMeetingClick}
      />
    </div>
  );
};

export const MeetingsMobilePendingPanel = () => {
  const navigate = useNavigate();
  const { pendingMeetings } = useMeetingsForMobilePanels();

  const handleMeetingClick = (meeting) => {
    if (!meeting?.id) return;
    const meetingSegment = meeting.shortSlug ?? meeting.id;
    navigate(`/app/overview/meeting/${meetingSegment}`, { state: { meeting } });
  };

  return (
    <div className="overview-mobile-panel-page">
      <PendingMeetingsCard
        pendingMeetings={pendingMeetings}
        onMeetingClick={handleMeetingClick}
      />
    </div>
  );
};
